import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderKanban, Target, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  belum_mulai: { label: "Belum mulai", cls: "" },
  terkirim: { label: "Terkirim", cls: "bg-amber-100 text-amber-700 border-0" },
  dinilai: { label: "Dinilai", cls: "bg-emerald-100 text-emerald-700 border-0" },
};

export default function Pjbl() {
  const qc = useQueryClient();
  const { data: projects = [], isLoading } = useQuery({ queryKey: ["pjbl-projects"], queryFn: () => api.get("/pjbl/projects").then((r) => r.data) });
  const { data: experiments = [] } = useQuery({ queryKey: ["my-experiments"], queryFn: () => api.get("/lab/experiments").then((r) => r.data) });

  const [active, setActive] = useState(null);
  const [analysis, setAnalysis] = useState("");
  const [expId, setExpId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const open = async (p) => {
    const { data } = await api.get(`/pjbl/projects/${p.id}`);
    setActive(data);
    setAnalysis(data.my_submission?.student_analysis || "");
    setExpId(data.my_submission?.experiment_id || "");
  };

  const submit = async () => {
    if (!analysis.trim()) { toast.error("Tulis analisis Anda terlebih dahulu"); return; }
    setSubmitting(true);
    try {
      await api.post(`/pjbl/projects/${active.id}/submit`, { student_analysis: analysis, experiment_id: expId || null });
      toast.success("Submission terkirim");
      setActive(null);
      qc.invalidateQueries({ queryKey: ["pjbl-projects"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
          <FolderKanban className="h-7 w-7 text-primary" /> Project-Based Learning
        </h1>
        <p className="text-muted-foreground mt-1">Selesaikan proyek, jalankan eksperimen pendukung, dan kirim analisis Anda.</p>
      </div>

      {isLoading && <p className="text-muted-foreground">Memuat…</p>}

      <div className="grid md:grid-cols-2 gap-5">
        {projects.map((p) => {
          const st = STATUS[p.my_status || "belum_mulai"];
          return (
            <Card key={p.id} className="rounded-md p-5 flex flex-col" data-testid={`project-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-heading font-bold text-lg">{p.title}</h3>
                <Badge className={st.cls} variant={st.cls ? "default" : "outline"}>{st.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-3 flex-1">{p.brief}</p>
              {p.my_score != null && <div className="font-data text-sm mt-2">Nilai: <span className="font-semibold">{p.my_score}</span></div>}
              <Button variant="outline" className="mt-4" onClick={() => open(p)} data-testid={`open-project-${p.id}`}>
                {p.my_status === "dinilai" ? "Lihat Nilai" : "Kerjakan"}
              </Button>
            </Card>
          );
        })}
      </div>
      {projects.length === 0 && !isLoading && <p className="text-muted-foreground">Belum ada proyek.</p>}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-xl">{active.title}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-1">Deskripsi Masalah</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">{active.brief}</p>
                </div>
                {active.objectives?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-1 flex items-center gap-1"><Target className="h-4 w-4" /> Tujuan</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                      {active.objectives.map((o, i) => <li key={i}>{o}</li>)}
                    </ul>
                  </div>
                )}
                {active.rubric?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-1">Rubrik Penilaian</h4>
                    <div className="space-y-1">
                      {active.rubric.map((r, i) => (
                        <div key={i} className="flex justify-between font-data text-xs bg-secondary rounded px-2.5 py-1.5">
                          <span>{r.criterion}</span><span>maks {r.max_score}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {active.my_submission?.status === "dinilai" ? (
                  <Card className="p-4 rounded-md bg-emerald-50 border-emerald-200">
                    <div className="flex items-center gap-2 font-semibold text-emerald-700 mb-2"><CheckCircle2 className="h-4 w-4" /> Sudah Dinilai — Total {active.my_submission.total_score}</div>
                    {active.my_submission.grade_scores?.map((g, i) => (
                      <div key={i} className="flex justify-between text-xs font-data"><span>{g.criterion}</span><span>{g.score}</span></div>
                    ))}
                    {active.my_submission.feedback && <p className="text-sm mt-2 italic">"{active.my_submission.feedback}"</p>}
                    <div className="mt-3">
                      <Label className="text-xs">Analisis Anda</Label>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{active.my_submission.student_analysis}</p>
                    </div>
                  </Card>
                ) : (
                  <div className="space-y-3 border-t border-border pt-4">
                    <h4 className="font-semibold">Pengumpulan</h4>
                    <div className="space-y-1">
                      <Label className="text-xs">Eksperimen pendukung (opsional)</Label>
                      <Select value={expId} onValueChange={setExpId}>
                        <SelectTrigger data-testid="pjbl-experiment-select"><SelectValue placeholder="Pilih eksperimen" /></SelectTrigger>
                        <SelectContent>
                          {experiments.map((e) => (
                            <SelectItem key={e.id} value={e.id}>{new Date(e.created_at).toLocaleString("id-ID")} · skor {e.score ?? "–"}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Analisis & Kesimpulan</Label>
                      <Textarea rows={6} value={analysis} onChange={(e) => setAnalysis(e.target.value)} placeholder="Jelaskan metode, hasil, dan interpretasi Anda…" data-testid="pjbl-analysis-input" />
                    </div>
                  </div>
                )}
              </div>
              {active.my_submission?.status !== "dinilai" && (
                <DialogFooter>
                  <Button onClick={submit} disabled={submitting} data-testid="pjbl-submit-button">
                    {active.my_submission ? "Perbarui Submission" : "Kirim Submission"}
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
